/* =============================================
   PRODUCT QUICK ADD — ATK BRASIL
   Web Component: <product-quick-add>
   Com suporte a Patches + Controle de Estoque
   ============================================= */

class ProductQuickAdd extends HTMLElement {
  constructor() {
    super();
    this.panel = null;
    this.body = null;
    this.overlay = null;
    this.closeBtn = null;

    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleOptionChange = this.handleOptionChange.bind(this);
  }

  connectedCallback() {
    this.panel = this.querySelector('[data-pqa-panel]');
    this.body = this.querySelector('[data-pqa-body]');
    this.overlay = this.querySelector('[data-pqa-overlay]');
    this.closeBtn = this.querySelector('[data-pqa-close]');

    this.setAttribute('aria-hidden', 'true');

    this.closeBtn?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', this.handleKeydown);
    this.addEventListener('submit', this.handleSubmit);
    this.addEventListener('change', this.handleOptionChange);
    this.addEventListener('input', this.handleOptionChange);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.handleKeydown);
  }

  openWithHandle(handle) {
    if (!handle) return;
    this.currentHandle = handle;
    this.open();
    this.renderLoading();
    this.fetchAndRender(handle);
  }

  open() {
    this.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('overflow-hidden');
  }

  close() {
    this.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('overflow-hidden');
    setTimeout(() => { if (this.body) this.body.innerHTML = ''; }, 350);
  }

  handleKeydown(e) {
    if (e.key === 'Escape' && this.getAttribute('aria-hidden') === 'false') this.close();
  }

  renderLoading() {
    if (!this.body) return;
    this.body.innerHTML = '<div class="pqa__loading"><svg class="pqa__loading-spinner" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2.5" fill="none" stroke-dasharray="31.4 31.4" stroke-linecap="round"/></svg><span>Carregando produto...</span></div>';
  }

  /* ── Fetch via Section Rendering API ── */
  async fetchAndRender(handle) {
    try {
      const root = window.Shopify?.routes?.root || '/';

      const [qaRes, mpRes] = await Promise.all([
        fetch(root + 'products/' + encodeURIComponent(handle) + '?section_id=product-quick-add', {
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        }),
        fetch(root + 'products/' + encodeURIComponent(handle) + '?section_id=main-product', {
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        })
      ]);

      if (!qaRes.ok) throw new Error('Erro ao carregar produto');

      const qaHtml = await qaRes.text();
      const qaDoc = new DOMParser().parseFromString(qaHtml, 'text/html');
      const content = qaDoc.querySelector('[data-pqa-content]');
      if (!content) throw new Error('Conteúdo não encontrado');

      this.body.innerHTML = '';
      this.body.appendChild(content);

      if (mpRes.ok) {
        const mpHtml = await mpRes.text();
        const mpDoc = new DOMParser().parseFromString(mpHtml, 'text/html');
        this.injectPatches(mpDoc);
      }

      this.initVariantLogic();
    } catch (err) {
      console.error('[PQA]', err);
      if (this.body) this.body.innerHTML = '<div class="pqa__empty">Não foi possível carregar o produto.</div>';
    }
  }

  /* ── Injetar patches do main-product ── */
  injectPatches(mpDoc) {
    const patchContainers = mpDoc.querySelectorAll('.patch-container');
    if (!patchContainers.length) return;

    const form = this.querySelector('[data-pqa-form]');
    if (!form) return;

    const patchWrapper = document.createElement('div');
    patchWrapper.className = 'pqa__patches';
    patchWrapper.innerHTML = '<p class="pqa__patches-title">Patches (opcional)</p>';

    patchContainers.forEach((container, index) => {
      const clone = container.cloneNode(true);
      const checkbox = clone.querySelector('.patch-checkbox-input');
      if (checkbox) {
        const newId = 'pqa-patch-' + index;
        checkbox.id = newId;
        checkbox.name = 'pqa_patch_items[]';
        const label = clone.querySelector('label');
        if (label) label.setAttribute('for', newId);
      }
      patchWrapper.appendChild(clone);
    });

    form.parentNode.insertBefore(patchWrapper, form);
  }

  /* ══════════════════════════════════════════════
     CONTROLE DE ESTOQUE POR VARIANTE
     ══════════════════════════════════════════════ */

  initVariantLogic() {
    const product = this.getProductJSON();
    if (!product) return;

    /* Guardar referência do product pra uso interno */
    this._product = product;

    const firstAvailable = product.variants.find(v => v.available) || product.variants[0];
    if (!firstAvailable) return;

    /* Setar a primeira variante disponível nos selects */
    const selects = this.querySelectorAll('.pqa__select[data-option-position]');
    selects.forEach(sel => {
      const pos = Number(sel.getAttribute('data-option-position'));
      const val = firstAvailable['option' + pos];
      if (val != null) sel.value = val;
    });

    const idInput = this.querySelector('form[data-pqa-form] input[name="id"]');
    if (idInput) idInput.value = firstAvailable.id;

    /* Atualizar disponibilidade de todas as opções */
    this.updateOptionAvailability();
    this.updateUI(firstAvailable, product);
    this.checkPersonalizacao();
  }

  /* 
   * Verifica para cada opção quais valores estão disponíveis
   * com base nas seleções atuais das OUTRAS opções.
   * 
   * Lógica: Para o tamanho M estar disponível, precisa existir
   * pelo menos 1 variante com tamanho=M + as demais opções 
   * selecionadas que esteja available=true.
   */
  updateOptionAvailability() {
    const product = this._product;
    if (!product || !product.variants) return;

    const selects = Array.from(this.querySelectorAll('.pqa__select[data-option-position]'));
    if (!selects.length) return;

    /* Pegar seleções atuais */
    const currentSelections = {};
    selects.forEach(sel => {
      const pos = Number(sel.getAttribute('data-option-position'));
      currentSelections[pos] = sel.value;
    });

    /* Para cada select, verificar disponibilidade de cada option */
    selects.forEach(sel => {
      const thisPos = Number(sel.getAttribute('data-option-position'));
      const options = sel.querySelectorAll('option');

      options.forEach(opt => {
        /* Pular o placeholder "Selecione..." */
        if (!opt.value) return;

        const testValue = opt.value;

        /* Verificar se existe alguma variante available com este valor
           E que combine com as seleções atuais das OUTRAS opções */
        const isAvailable = product.variants.some(variant => {
          /* Deve ter este valor na posição atual */
          if (variant['option' + thisPos] !== testValue) return false;

          /* Deve combinar com as outras opções selecionadas */
          for (const [posStr, selectedVal] of Object.entries(currentSelections)) {
            const pos = Number(posStr);
            if (pos === thisPos) continue; /* Pular a própria posição */
            if (!selectedVal) continue; /* Se não selecionou, ignora */
            if (variant['option' + pos] !== selectedVal) return false;
          }

          /* Deve estar disponível em estoque */
          return variant.available;
        });

        /* Também verificar disponibilidade "global" (sem considerar outras opções)
           para mostrar quais tamanhos existem em ALGUMA combinação */
        const existsInAny = product.variants.some(variant => {
          return variant['option' + thisPos] === testValue && variant.available;
        });

        /* Atualizar a opção */
        const originalText = opt.getAttribute('data-original-text') || opt.textContent.replace(/\s*\(Esgotado\)\s*$/, '').trim();
        opt.setAttribute('data-original-text', originalText);

        if (!existsInAny) {
          /* Totalmente esgotado — sem estoque em NENHUMA combinação */
          opt.textContent = originalText + ' (Esgotado)';
          opt.disabled = true;
          opt.style.color = '#999';
          opt.setAttribute('data-unavailable', 'true');
        } else if (!isAvailable) {
          /* Esgotado nesta combinação específica */
          opt.textContent = originalText + ' (Esgotado nesta combinação)';
          opt.disabled = true;
          opt.style.color = '#999';
          opt.setAttribute('data-unavailable', 'combo');
        } else {
          /* Disponível */
          opt.textContent = originalText;
          opt.disabled = false;
          opt.style.color = '';
          opt.removeAttribute('data-unavailable');
        }
      });

      /* Se o valor selecionado ficou disabled, tentar selecionar o primeiro disponível */
      const currentOpt = sel.querySelector('option[value="' + CSS.escape(sel.value) + '"]');
      if (currentOpt && currentOpt.disabled) {
        const firstAvailableOpt = sel.querySelector('option:not([disabled]):not([value=""])');
        if (firstAvailableOpt) {
          sel.value = firstAvailableOpt.value;
        }
      }
    });
  }

  /* ── Helpers JSON ── */
  getProductJSON() {
    const el = this.querySelector('[data-pqa-product-json]');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch { return null; }
  }

  getPersonalizarPosition() {
    const el = this.querySelector('[data-pqa-personalizar-position]');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch { return null; }
  }

  /* ── Encontrar variante selecionada ── */
  getSelectedVariant(product) {
    if (!product) return null;
    const selects = Array.from(this.querySelectorAll('.pqa__select[data-option-position]'));
    if (!selects.length) return product.variants[0];

    const chosen = selects
      .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
      .map(s => s.value);

    if (chosen.some(v => !v)) return null;

    return product.variants.find(v =>
      chosen.every((val, i) => v['option' + (i + 1)] === val)
    ) || null;
  }

  /* ── Change handler (com atualização de disponibilidade) ── */
  handleOptionChange(e) {
    const target = e.target;
    if (!target.matches('.pqa__select[data-option-position]') && !target.matches('.pqa__input')) return;

    const product = this._product || this.getProductJSON();

    /* Atualizar disponibilidade das outras opções com base na nova seleção */
    if (target.matches('.pqa__select[data-option-position]')) {
      this.updateOptionAvailability();
    }

    const variant = this.getSelectedVariant(product);

    if (variant) {
      const idInput = this.querySelector('form[data-pqa-form] input[name="id"]');
      if (idInput) idInput.value = variant.id;
    }

    this.updateUI(variant, product);
    this.checkPersonalizacao();
  }

  /* ── Atualizar UI (preço, imagem, botão) ── */
  updateUI(variant, product) {
    const btn = this.querySelector('.pqa__atc');
    const btnText = this.querySelector('.pqa__atc-text');
    const priceEl = this.querySelector('.pqa__price');
    const imageEl = this.querySelector('.pqa__image');

    if (!btn) return;

    if (!variant) {
      btn.disabled = true;
      if (btnText) btnText.textContent = 'SELECIONE AS OPÇÕES';
      return;
    }

    if (variant.available) {
      btn.disabled = false;
      if (btnText) btnText.textContent = 'ADICIONAR AO CARRINHO';
    } else {
      btn.disabled = true;
      if (btnText) btnText.textContent = 'INDISPONÍVEL';
    }

    if (priceEl) {
      let html = '';
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        html += '<span class="pqa__price-compare">' + this.formatMoney(variant.compare_at_price) + '</span>';
      }
      html += '<span class="pqa__price-current">' + this.formatMoney(variant.price) + '</span>';
      if (variant.price > 0) {
        html += '<span class="pqa__price-installment">ou 3x de <strong>' + this.formatMoney(Math.round(variant.price / 3)) + '</strong></span>';
      }
      priceEl.innerHTML = html;
    }

    if (imageEl && variant.featured_image && variant.featured_image.src) {
      const newSrc = variant.featured_image.src.replace(/(\.[^.]+)$/, '_800x$1');
      if (imageEl.src !== newSrc) {
        imageEl.style.opacity = '0.5';
        const img = new Image();
        img.onload = () => { imageEl.src = newSrc; imageEl.style.opacity = '1'; };
        img.src = newSrc;
      }
    }
  }

  /* ── Mostrar/ocultar campos de personalização ── */
  checkPersonalizacao() {
    const pos = this.getPersonalizarPosition();
    if (!pos) return;

    const sel = this.querySelector('.pqa__select[data-option-position="' + pos + '"]');
    const wrapper = this.querySelector('#pqa-personalizacao');
    if (!sel || !wrapper) return;

    const show = sel.value.toLowerCase().trim().includes('sim');
    wrapper.style.display = show ? '' : 'none';
    if (!show) wrapper.querySelectorAll('.pqa__input').forEach(i => { i.value = ''; });
  }

  formatMoney(cents) {
    const value = (cents / 100).toFixed(2).replace('.', ',');
    return 'R$ ' + value.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }

  getCheckedPatches() {
    return Array.from(this.querySelectorAll('.pqa__patches .patch-checkbox-input:checked'))
      .map(cb => ({ id: parseInt(cb.value), quantity: 1 }));
  }

  /* ── Submit: Add to Cart ── */
  async handleSubmit(e) {
    const form = e.target;
    if (!form.matches('[data-pqa-form]')) return;
    e.preventDefault();

    const btn = form.querySelector('.pqa__atc');
    const statusEl = this.querySelector('[data-pqa-status]');
    const root = window.Shopify?.routes?.root || '/';

    const product = this._product || this.getProductJSON();
    const variant = this.getSelectedVariant(product);
    if (!variant) {
      if (statusEl) statusEl.textContent = 'Selecione todas as opções antes de adicionar.';
      return;
    }

    if (!variant.available) {
      if (statusEl) statusEl.textContent = 'Esta combinação está esgotada. Escolha outra opção.';
      return;
    }

    const patches = this.getCheckedPatches();
    const hasPatches = patches.length > 0;

    const properties = {};
    const persWrapper = this.querySelector('#pqa-personalizacao');
    if (persWrapper && persWrapper.style.display !== 'none') {
      const nome = persWrapper.querySelector('#pqa-prop-nome');
      const num = persWrapper.querySelector('#pqa-prop-numero');
      if (nome && nome.value) properties['Nome'] = nome.value;
      if (num && num.value) properties['Número'] = num.value;
    }

    try {
      if (btn) { btn.setAttribute('data-state', 'loading'); btn.disabled = true; }
      if (statusEl) statusEl.textContent = '';

      let data;

      if (hasPatches) {
        const mainItem = { id: parseInt(variant.id), quantity: 1 };
        if (Object.keys(properties).length > 0) mainItem.properties = properties;

        const items = [mainItem, ...patches];

        const res = await fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: items, sections: 'cart-drawer,cart-icon-bubble' })
        });

        data = await res.json().catch(() => ({}));
        if (data && data.status && data.status !== 200) throw new Error(data.description || data.message || 'Erro ao adicionar.');
        if (data && data.sections) this.applySections(data.sections);

      } else {
        const formData = new FormData(form);
        Object.entries(properties).forEach(([k, v]) => formData.set('properties[' + k + ']', v));

        const cart = document.querySelector('cart-drawer');
        if (cart && typeof cart.getSectionsToRender === 'function') {
          formData.append('sections', cart.getSectionsToRender().map(s => s.id));
          formData.append('sections_url', window.location.pathname);
          if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
        }

        const res = await fetch(window.routes?.cart_add_url || root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData
        });

        data = await res.json().catch(() => ({}));
        if (data && data.status) throw new Error(data.description || data.message || 'Erro ao adicionar.');

        if (cart && data && data.sections && typeof cart.getSectionInnerHTML === 'function') {
          cart.classList.remove('is-empty');
          cart.getSectionsToRender().forEach(section => {
            const el = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
            if (!el) return;
            el.innerHTML = cart.getSectionInnerHTML(data.sections[section.id], section.selector);
          });
        }
      }

      if (btn) btn.setAttribute('data-state', 'success');

      setTimeout(() => {
        this.close();
        if (btn) { btn.removeAttribute('data-state'); btn.disabled = false; }

        setTimeout(() => {
          const cart = document.querySelector('cart-drawer');
          if (cart) {
            if (typeof cart.open === 'function') cart.open();
            else {
              const icon = document.querySelector('cart-icon-bubble a, [data-cart-icon], .header__icon--cart');
              if (icon) icon.click();
            }
          }
        }, 400);
      }, 800);

    } catch (err) {
      console.error('[PQA] Add to cart error:', err);
      if (statusEl) statusEl.textContent = err.message || 'Não foi possível adicionar. Tente novamente.';
      if (btn) { btn.removeAttribute('data-state'); btn.disabled = false; }
    }
  }

  applySections(sections) {
    if (sections['cart-drawer']) {
      const doc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      const newDrawer = doc.querySelector('cart-drawer') || doc.querySelector('#CartDrawer');
      const current = document.querySelector('cart-drawer');
      if (newDrawer && current) { current.innerHTML = newDrawer.innerHTML; current.classList.remove('is-empty'); }
    }
    if (sections['cart-icon-bubble']) {
      const doc = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
      const newBubble = doc.querySelector('#cart-icon-bubble');
      const current = document.querySelector('#cart-icon-bubble');
      if (newBubble && current) current.innerHTML = newBubble.innerHTML;
    }
  }
}

customElements.define('product-quick-add', ProductQuickAdd);

window.openQuickAdd = function(handle) {
  const modal = document.querySelector('product-quick-add');
  if (!modal) return;
  modal.openWithHandle(handle);
};
